// Save text interview answer list **************************************************************
// pathname: /v2/job-interview/save-text-answer-list
// ************************************************************************************************
import { createElement, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { bjToast } from '@components/common';
import { useSwrMutationApi, GenericV1ErrorsArray } from 'src/hooks/useSwrApi';

interface UseSwrJobInterviewSaveTextAnswerListProps {
  locale?: string;
  successCallback?: (response: ApiSchema<'InterviewTextAnswerListRead'>) => void;
  errorCallback?: (error: GenericV1ErrorsArray) => void;
}

/**
 * @description SWR hook: Save text interview answer list
 * - POST /v2/job-interview/save-text-answer-list
 * - @param locale - The current app locale. Default: 'ro'
 * - @param successCallback - Function to call on successful save
 * - @param errorCallback - Function to call on error
 *
 * @example
 * const { data, trigger, isMutating, error } = useSwrJobInterviewSaveTextAnswerList({
 *   locale: 'ro',
 *   successCallback: (response) => {
 *     console.log('Answers saved:', response);
 *   },
 *   errorCallback: (error) => {
 *     console.error('Failed to save:', error);
 *   }
 * });
 *
 * // Trigger the save
 * trigger({
 *   textAnswers: [
 *     { questionId: 1, answerText: 'My answer' }
 *   ]
 * });
 */
export const useSwrJobInterviewSaveTextAnswerList = (
  props: UseSwrJobInterviewSaveTextAnswerListProps = {},
) => {
  const {
    locale = 'ro',
    successCallback,
    errorCallback,
  } = props;
  const { t } = useTranslation('common');

  const swr = useSwrMutationApi<ApiSchema<'InterviewTextAnswerListRead'>, ApiSchema<'InterviewTextAnswerListWrite4'>>({
    url: '/v2/job-interview/save-text-answer-list',
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
    apiOptions: {
      onSuccess: (response) => {
        if (successCallback) {
          successCallback(response);
        }
      },
      onError: (error) => {
        if (errorCallback) {
          errorCallback(error);
        }
      },
    },
    toastsOptions: {
      showSuccessToast: true,
    },
  });

  // SWR's `use` middleware (which `useSwrMutationApi` wires up) is silently
  // ignored by useSWRMutation — middleware is a useSWR-only feature. So the
  // shared `showErrorToast` plumbing never fires for mutations; surface the
  // backend validation messages directly here.
  // Using createElement (instead of JSX) keeps this file as .ts to avoid
  // accidental extension churn.
  useEffect(() => {
    if (!swr.error) return;
    const messages = Array.from(new Set(
      swr.error.errors?.map((e) => e.message).filter(Boolean) ?? [],
    ));
    if (messages.length === 0) {
      bjToast.error(t('global.error.generic'));
      return;
    }
    if (messages.length === 1) {
      bjToast.error(messages[0]);
      return;
    }
    bjToast.error(
      createElement(
        'ul',
        { className: 'list-disc pl-4' },
        messages.map((m) => createElement('li', { key: m }, m)),
      ),
    );
  }, [swr.error, t]);

  return swr;
};
